import { motion } from "framer-motion";
import { FaWindows, FaLinux, FaAndroid, FaApple } from "react-icons/fa";
import AnimatedNumber from "./AnimatedNumber";
import dev from "../models/joinDevice.json";
import "./HudModules.css";

/* Deterministic hash so radar blips stay stable across polls */
const hashId = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
};

/* ===== Circular arc gauge with rotating reticle ===== */
export const ArcGauge = ({ value = 0, label, unit = "%", tone = "accent" }) => {
  const R = 38;
  const CIRC = 2 * Math.PI * R;
  const pct = Math.max(0, Math.min(value / 100, 1));

  return (
    <div className={`hud-gauge hud-gauge--${tone}`}>
      <div className="hud-gauge__stage">
        <span className="hud-gauge__reticle" />
        <svg viewBox="0 0 100 100" className="hud-gauge__svg">
          {/* tick marks */}
          {[...Array(12)].map((_, i) => (
            <line
              key={i}
              className="hud-gauge__tick"
              x1="50"
              y1="4"
              x2="50"
              y2="9"
              transform={`rotate(${i * 30} 50 50)`}
            />
          ))}
          <circle className="hud-gauge__track" cx="50" cy="50" r={R} />
          <motion.circle
            className="hud-gauge__fill"
            cx="50"
            cy="50"
            r={R}
            strokeDasharray={CIRC}
            initial={{ strokeDashoffset: CIRC }}
            animate={{ strokeDashoffset: CIRC * (1 - pct) }}
            transition={{ type: "spring", stiffness: 60, damping: 18 }}
            transform="rotate(-90 50 50)"
          />
        </svg>
        <div className="hud-gauge__center hud-mono">
          <span className="hud-gauge__value">
            <AnimatedNumber value={Math.round(value)} />
          </span>
          <span className="hud-gauge__unit">{unit}</span>
        </div>
      </div>
      <span className="hud-gauge__label hud-mono">{label}</span>
    </div>
  );
};

/* ===== Radar / sonar sweep with device blips ===== */
export const HoloRadar = ({ devices = [], selectedDevice }) => {
  return (
    <div className="hud-radar">
      <div className="hud-radar__stage">
        <svg viewBox="0 0 100 100" className="hud-radar__grid">
          <circle cx="50" cy="50" r="48" />
          <circle cx="50" cy="50" r="33" />
          <circle cx="50" cy="50" r="18" />
          <line x1="50" y1="2" x2="50" y2="98" />
          <line x1="2" y1="50" x2="98" y2="50" />
          <line x1="16" y1="16" x2="84" y2="84" className="hud-radar__diag" />
          <line x1="84" y1="16" x2="16" y2="84" className="hud-radar__diag" />
        </svg>
        <div className="hud-radar__sweep" />
        {devices.map((d) => {
          const h = hashId(d[dev.id]);
          const angle = (h % 360) * (Math.PI / 180);
          const radius = 12 + (h >> 5) % 33;
          const x = 50 + radius * Math.cos(angle);
          const y = 50 + radius * Math.sin(angle);
          const isOn = d[dev.state] === dev.typestates.on;
          const isSel = d[dev.id] === selectedDevice;
          return (
            <span
              key={d[dev.id]}
              className={`hud-radar__blip ${
                isOn ? "hud-radar__blip--on" : "hud-radar__blip--off"
              } ${isSel ? "hud-radar__blip--locked" : ""}`}
              style={{ left: `${x}%`, top: `${y}%` }}
              title={d[dev.id]}
            />
          );
        })}
      </div>
      <span className="hud-gauge__label hud-mono">NET.SCAN</span>
    </div>
  );
};

/* ===== Telemetry oscilloscope ===== */
export const Oscilloscope = ({ selectedDevice }) => {
  return (
    <div className="hud-scope">
      <div className="hud-scope__stage">
        <svg viewBox="0 0 200 60" preserveAspectRatio="none">
          {/* grid */}
          {[15, 30, 45].map((y) => (
            <line key={y} className="hud-scope__grid" x1="0" y1={y} x2="200" y2={y} />
          ))}
          {[40, 80, 120, 160].map((x) => (
            <line key={x} className="hud-scope__grid" x1={x} y1="0" x2={x} y2="60" />
          ))}
          <path
            className="hud-scope__wave"
            d="M0,30 L22,30 L28,16 L36,44 L42,30 L70,30 L76,22 L82,38 L88,30 L118,30 L124,8 L132,50 L140,30 L168,30 L174,20 L180,40 L186,30 L200,30"
          />
        </svg>
        <span className="hud-scope__sweepline" />
      </div>
      <span className="hud-gauge__label hud-mono">
        {selectedDevice ? (
          <>
            TRACE:&nbsp;<b className="hud-accent-text">{selectedDevice}</b>
          </>
        ) : (
          "TELEMETRY"
        )}
      </span>
    </div>
  );
};

/* ===== Platform breakdown (devices by OS type) ===== */
export const PlatformBreakdown = ({ devices = [] }) => {
  const platforms = [
    { type: dev.types.windows, Icon: FaWindows, cls: "windows" },
    { type: dev.types.linux, Icon: FaLinux, cls: "linux" },
    { type: dev.types.android, Icon: FaAndroid, cls: "android" },
    { type: dev.types.apple, Icon: FaApple, cls: "apple" },
  ];
  return (
    <div className="hud-platforms">
      {platforms.map(({ type, Icon, cls }) => {
        const count = devices.filter((d) => d[dev.type] === type).length;
        return (
          <div
            key={type}
            className={`hud-platform hud-platform--${cls} ${
              count === 0 ? "hud-platform--empty" : ""
            }`}
            title={type}
          >
            <Icon className="hud-platform__icon" />
            <span className="hud-platform__count hud-mono">
              <AnimatedNumber value={count} />
            </span>
          </div>
        );
      })}
    </div>
  );
};

/* ===== Composite top strip: nodes | gauges | platforms ===== */
const HudModules = ({ devices = [] }) => {
  const online = devices.filter(
    (d) => d[dev.state] === dev.typestates.on
  ).length;
  const onlinePct = devices.length > 0 ? (online / devices.length) * 100 : 0;
  const batteries = devices
    .map((d) => parseInt(d.battery))
    .filter((v) => !isNaN(v));
  const avgBattery =
    batteries.length > 0
      ? batteries.reduce((a, b) => a + b, 0) / batteries.length
      : 0;

  return (
    <div className="hud-modules">
      <div className="hud-module">
        <span className="hud-module__caption hud-mono">{"// NODE.STATUS"}</span>
        <div className="hud-nodes hud-mono">
          <div className="hud-nodes__stat">
            <span className="hud-nodes__value">
              <AnimatedNumber value={devices.length} />
            </span>
            <span className="hud-nodes__label">NODES</span>
          </div>
          <div className="hud-nodes__stat">
            <span className="hud-nodes__value hud-nodes__value--up">
              <AnimatedNumber value={online} />
            </span>
            <span className="hud-nodes__label">UP</span>
          </div>
          <div className="hud-nodes__stat">
            <span className="hud-nodes__value hud-nodes__value--dn">
              <AnimatedNumber value={devices.length - online} />
            </span>
            <span className="hud-nodes__label">DN</span>
          </div>
        </div>
      </div>
      <div className="hud-module hud-module--gauges">
        <span className="hud-module__caption hud-mono">{"// SYS.STATUS"}</span>
        <div className="hud-module__gauges">
          <ArcGauge value={onlinePct} label="UPLINK" tone="success" />
          <ArcGauge value={avgBattery} label="PWR.AVG" tone="warning" />
        </div>
      </div>
      <div className="hud-module">
        <span className="hud-module__caption hud-mono">{"// PLATFORMS"}</span>
        <PlatformBreakdown devices={devices} />
      </div>
    </div>
  );
};

export default HudModules;
